/**
 * What the node colours mean: a small "Legend" button in the graph's corner
 * that opens an explanation, instead of a key that is always on screen.
 *
 * The always-on legend answered a question the student asks once, and then
 * sat over the map for every visit after. Now the map is clear by default and
 * the explanation is one click away, with room to say what each state MEANS
 * for the student, not just its name.
 *
 * The swatches are painted by `canvasPalette().nodeTier`, the exact function
 * the canvas paints with, and the names and recall ranges come from
 * `TEXTURE_LEGEND` and `recallRange`, which the concept panel and
 * `memoryTier` use too. So the explanation cannot drift from the map it
 * explains.
 *
 * A "Paused" row joins them only while a paused concept is on the map, drawn
 * hollow like the node itself. `fresh` is deliberately absent: it draws as
 * `strong`, and listing it would promise a distinction the map does not make.
 */

import * as Popover from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIsDarkMode } from '@/hooks/ui/themeClass';
import {
  PAUSED_ALPHA,
  canvasPalette,
} from '@/components/features/mindspace/graph/graphPalette';
import {
  TEXTURE_LEGEND,
  recallRange,
  type MemoryTier,
} from '@/lib/mindspace/memoryTier';

/** One line per state: what it means for the student, in the second person. */
const MEANING: Partial<Record<MemoryTier, string>> = {
  strong: 'You know this well right now. Nothing to do.',
  fading: 'Starting to slip. A quick review now keeps it.',
  due: 'Likely to be forgotten soon. Reviewing it brings it back.',
  new: 'A lesson added it to your archive, but you have not studied it yet.',
};

const PAUSED_MEANING = 'Left out of your reviews until you resume it.';

export function GraphLegend({ showPaused = false }: { showPaused?: boolean }) {
  const isDark = useIsDarkMode();
  const { nodeTier, nodeInk } = canvasPalette(isDark);

  return (
    <Popover.Root>
      <Popover.Trigger
        // Positioned by the toolbar it sits in (`GraphControls`).
        className='inline-flex h-8 items-center gap-2 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
        // No aria-label: the visible word is the accessible name, so a
        // voice-control user can say what they see.
      >
        {/* The four colours in a row: a preview of what the button explains. */}
        <span aria-hidden='true' className='flex -space-x-0.5'>
          {TEXTURE_LEGEND.map(({ tier }) => (
            <span
              key={tier}
              className='h-2 w-2 rounded-full ring-1 ring-card'
              style={{ backgroundColor: nodeTier(tier) }}
            />
          ))}
        </span>
        Legend
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side='top'
          align='start'
          sideOffset={8}
          collisionPadding={16}
          aria-label='What the colours mean'
          className='z-40 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card p-4 text-left shadow-elevation-2 motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in-0'
        >
          <KeyHeader />

          <ul className='space-y-3'>
            {TEXTURE_LEGEND.map(({ tier, label }) => (
              <KeyRow
                key={tier}
                swatch={
                  <span
                    className='h-3 w-3 rounded-full'
                    style={{ backgroundColor: nodeTier(tier) }}
                  />
                }
                label={label}
                range={recallRange(tier)}
                meaning={MEANING[tier] ?? ''}
              />
            ))}
            {showPaused && (
              <KeyRow
                swatch={
                  // Hollow, like the node: paused is a shape, not a colour.
                  <span
                    className='h-3 w-3 rounded-full border-[3px]'
                    style={{ borderColor: nodeInk(PAUSED_ALPHA) }}
                  />
                }
                label='Paused'
                range={null}
                meaning={PAUSED_MEANING}
              />
            )}
          </ul>

          <p className='mt-4 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground'>
            Recall is worked out from your reviews. For a concept you have read
            but not reviewed yet, it is estimated from when you last studied it.
          </p>

          <p className='mt-2 text-xs leading-relaxed text-muted-foreground'>
            Each lesson&apos;s key ideas are joined into its own constellation.
            A line lights up once you have learned the ideas at both ends. Point
            at a concept to see everything it connects to.
          </p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function KeyHeader() {
  return (
    <div className='mb-3 flex items-start justify-between gap-3'>
      <div>
        <h2 className='text-sm font-semibold text-foreground'>
          What the colours mean
        </h2>
        <p className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>
          Each dot is a concept. Its colour is how likely you are to remember it
          today.
        </p>
      </div>
      <Popover.Close
        aria-label='Close'
        className='-mr-1 -mt-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
      >
        <X className='h-4 w-4' aria-hidden='true' />
      </Popover.Close>
    </div>
  );
}

function KeyRow({
  swatch,
  label,
  range,
  meaning,
}: {
  swatch: ReactNode;
  label: string;
  range: string | null;
  meaning: string;
}) {
  return (
    <li className='flex gap-2.5'>
      <span
        aria-hidden='true'
        className='mt-1 flex h-3 w-3 flex-shrink-0 items-center justify-center'
      >
        {swatch}
      </span>
      <div className='min-w-0 flex-1'>
        <p className='flex items-baseline justify-between gap-2 text-sm font-medium text-foreground'>
          {label}
          {range && (
            <span className='font-mono text-xs font-normal tabular-nums text-muted-foreground'>
              {range}
            </span>
          )}
        </p>
        <p className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>
          {meaning}
        </p>
      </div>
    </li>
  );
}
